import React, { useEffect, useRef } from 'react';

const BackgroundEffects: React.FC = () => {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas dimensions
        const updateCanvasDimensions = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        updateCanvasDimensions();
        window.addEventListener('resize', updateCanvasDimensions);

        // Star properties
        const stars: {
            x: number;
            y: number;
            radius: number;
            opacity: number;
            speed: number;
            twinkleSpeed: number;
            twinkleDirection: number;
        }[] = [];

        // Create stars - reduce the number of stars for better performance
        const createStars = () => {
            stars.length = 0;
            // Reduced density by half for better performance
            const count = Math.floor(canvas.width * canvas.height / 12000);

            const maxStars = Math.min(count, 300); // Limit max stars

            for (let i = 0; i < maxStars; i++) {
                stars.push({
                    x: Math.random() * canvas.width,
                    y: Math.random() * canvas.height,
                    radius: Math.random() * 1.5 + 0.5,
                    opacity: Math.random() * 0.5 + 0.1,
                    speed: Math.random() * 0.05 + 0.02,
                    twinkleSpeed: Math.random() * 0.01 + 0.002,
                    twinkleDirection: Math.random() > 0.5 ? 1 : -1,
                });
            }
        };

        createStars();
        window.addEventListener('resize', createStars);

        // Orb properties - reduce to 2 orbs instead of 3
        const orbs = [
            {
                x: canvas.width * 0.2,
                y: canvas.height * 0.3,
                radius: 150,
                color: 'rgba(99, 102, 241, 0.12)',
                speedX: 0.2,
                speedY: 0.15,
            },
            {
                x: canvas.width * 0.8,
                y: canvas.height * 0.7,
                radius: 200,
                color: 'rgba(139, 92, 246, 0.12)',
                speedX: -0.15,
                speedY: 0.1,
            }
        ];

        // Animation loop with throttling
        let animationFrameId: number;
        let lastFrameTime = 0;
        const targetFPS = 30; // Limit to 30 FPS for better performance
        const frameInterval = 1000 / targetFPS;

        const render = (timestamp: number) => {
            // Throttle animation frame rate
            const elapsed = timestamp - lastFrameTime;
            if (elapsed < frameInterval) {
                animationFrameId = requestAnimationFrame(render);
                return;
            }
            lastFrameTime = timestamp - (elapsed % frameInterval);

            try {
                ctx.clearRect(0, 0, canvas.width, canvas.height);

                // Draw orbs
                orbs.forEach((orb) => {
                    // Move orb
                    orb.x += orb.speedX;
                    orb.y += orb.speedY;

                    // Bounce off walls
                    if (orb.x < -orb.radius) orb.x = canvas.width + orb.radius;
                    if (orb.x > canvas.width + orb.radius) orb.x = -orb.radius;
                    if (orb.y < -orb.radius) orb.y = canvas.height + orb.radius;
                    if (orb.y > canvas.height + orb.radius) orb.y = -orb.radius;

                    // Draw orb
                    const gradient = ctx.createRadialGradient(
                        orb.x, orb.y, 0,
                        orb.x, orb.y, orb.radius
                    );
                    gradient.addColorStop(0, orb.color.replace('0.12', '0.2'));
                    gradient.addColorStop(1, orb.color.replace('0.12', '0'));

                    ctx.beginPath();
                    ctx.arc(orb.x, orb.y, orb.radius, 0, Math.PI * 2);
                    ctx.fillStyle = gradient;
                    ctx.fill();
                });

                // Draw stars
                stars.forEach((star) => {
                    // Twinkle effect
                    star.opacity += star.twinkleDirection * star.twinkleSpeed;

                    // Change direction if opacity limits reached
                    if (star.opacity <= 0.1 || star.opacity >= 0.6) {
                        star.twinkleDirection *= -1;
                    }

                    // Move star slowly upward
                    star.y -= star.speed;
                    // Wrap around if star goes off-screen
                    if (star.y < -10) star.y = canvas.height + 10;

                    // Draw star
                    ctx.beginPath();
                    ctx.arc(star.x, star.y, star.radius, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity})`;
                    ctx.fill();
                });
            } catch (error) {
                console.error('Error in BackgroundEffects animation:', error);
            }

            animationFrameId = requestAnimationFrame(render);
        };

        animationFrameId = requestAnimationFrame(render);

        // Cleanup
        return () => {
            window.removeEventListener('resize', updateCanvasDimensions);
            window.removeEventListener('resize', createStars);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className="fixed inset-0 -z-10"
            style={{ background: 'radial-gradient(ellipse at top, #1a1a2e, #0f0f1a)' }}
        />
    );
};

export default BackgroundEffects; 